// react
import { useCallback, useEffect, useState } from 'react';

// msal
import { useMsal } from '@azure/msal-react';

// types
import { VerificationGroup, VerificationGroupPayload } from '../@types';

// services
import {
	createVerificationGroup,
	deactivateVerificationGroup,
	fetchVerificationGroup,
	fetchVerificationGroups,
	updateVerificationGroup,
	ApiResponse,
	QueryParams,
} from '../services/api';

// Types

export interface UseVerificationGroupsParams {
	isEnabled?: boolean;
	projectId?: number;
	query?: QueryParams;
}

export interface UseVerificationGroupsReturn {
	create: (payload: VerificationGroupPayload) => Promise<ApiResponse<VerificationGroup>>;
	data: VerificationGroup[];
	deactivate: (id: number) => Promise<ApiResponse<VerificationGroup>>;
	error: string | null;
	loading: boolean;
	pagination: ApiResponse<VerificationGroup[]>['pagination'];
	refetch: () => Promise<void>;
	update: (
		id: number,
		payload: Partial<VerificationGroupPayload> & {
			addMembers?: { userId: number; role?: string }[];
			isActive?: boolean;
			removeMembers?: number[];
		}
	) => Promise<ApiResponse<VerificationGroup>>;
}

// Hook

export const useVerificationGroups = ({
	isEnabled = true,
	projectId,
	query = {},
}: UseVerificationGroupsParams = {}): UseVerificationGroupsReturn => {
	// auth
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const { accounts, instance } = useMsal();

	const refreshTokens = useCallback(async () => {
		if (accounts.length === 0) return null;
		try {
			const result = await instance.acquireTokenSilent({
				account: accounts[0],
				scopes: ['user.read'],
			});
			if (result.accessToken && result.idToken) {
				sessionStorage.setItem('accessToken', result.accessToken);
				sessionStorage.setItem('idToken', result.idToken);
				return { accessToken: result.accessToken, idToken: result.idToken };
			}
		} catch {
			// fall through to sessionStorage tokens
		}
		return null;
	}, [accounts, instance]);

	// state
	const [data, setData] = useState<VerificationGroup[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [pagination, setPagination] =
		useState<ApiResponse<VerificationGroup[]>['pagination']>(undefined);

	// fetch
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const queryKey = JSON.stringify(query);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken) return;

		setLoading(true);
		setError(null);

		try {
			const res = await fetchVerificationGroups(
				{ accessToken, idToken },
				{ ...query, populate: 'members', project: projectId }
			);

			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || []);
				setPagination(res.pagination);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch verification groups');
		} finally {
			setLoading(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [accessToken, idToken, projectId, queryKey]);

	// initial fetch
	useEffect(() => {
		if (isEnabled && projectId) {
			fetchData();
		}
	}, [isEnabled, fetchData, projectId]);

	// create
	const create = useCallback(
		async (payload: VerificationGroupPayload): Promise<ApiResponse<VerificationGroup>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await createVerificationGroup(tokens, payload);
				if (!res.error) await fetchData();
				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to create group';
				return { error: { id: 'CREATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// update
	const update = useCallback(
		async (
			id: number,
			payload: Partial<VerificationGroupPayload> & {
				addMembers?: { userId: number; role?: string }[];
				isActive?: boolean;
				removeMembers?: number[];
			}
		): Promise<ApiResponse<VerificationGroup>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await updateVerificationGroup(tokens, id, payload);
				if (!res.error) await fetchData();
				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update group';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	// deactivate (soft-delete via is_active = false)
	const deactivate = useCallback(
		async (id: number): Promise<ApiResponse<VerificationGroup>> => {
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}

			try {
				const res = await deactivateVerificationGroup(tokens, id);
				if (!res.error) await fetchData();
				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to deactivate group';
				return { error: { id: 'DEACTIVATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, fetchData, refreshTokens]
	);

	return {
		create,
		data,
		deactivate,
		error,
		loading,
		pagination,
		refetch: fetchData,
		update,
	};
};

// Single Group Hook (for detail page)

export interface UseVerificationGroupParams {
	id?: number;
	isEnabled?: boolean;
}

export const useVerificationGroup = ({ id, isEnabled = true }: UseVerificationGroupParams) => {
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');
	const { accounts, instance } = useMsal();

	const refreshTokens = useCallback(async () => {
		if (accounts.length === 0) return null;
		try {
			const result = await instance.acquireTokenSilent({
				account: accounts[0],
				scopes: ['user.read'],
			});
			if (result.accessToken && result.idToken) {
				sessionStorage.setItem('accessToken', result.accessToken);
				sessionStorage.setItem('idToken', result.idToken);
				return { accessToken: result.accessToken, idToken: result.idToken };
			}
		} catch {
			// fall through
		}
		return null;
	}, [accounts, instance]);

	const [data, setData] = useState<VerificationGroup | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const fetchData = useCallback(async () => {
		if (!accessToken || !idToken || !id) return;
		setLoading(true);
		setError(null);
		try {
			const res = await fetchVerificationGroup({ accessToken, idToken }, id, {
				populate: 'members',
			});
			if (res.error) {
				setError(res.error.message);
			} else {
				setData(res.data || null);
			}
		} catch (err) {
			setError(err instanceof Error ? err.message : 'Failed to fetch group');
		} finally {
			setLoading(false);
		}
	}, [accessToken, idToken, id]);

	useEffect(() => {
		if (isEnabled && id) {
			fetchData();
		}
	}, [isEnabled, fetchData, id]);

	const update = useCallback(
		async (
			payload: Partial<VerificationGroupPayload> & {
				addMembers?: { userId: number; role?: string }[];
				isActive?: boolean;
				removeMembers?: number[];
			}
		): Promise<ApiResponse<VerificationGroup>> => {
			if (!id) return { error: { id: 'NO_ID', message: 'No group ID' }, status: 400 };
			const tokens =
				(await refreshTokens()) || (accessToken && idToken ? { accessToken, idToken } : null);
			if (!tokens) {
				return { error: { id: 'UNAUTHORIZED', message: 'Not authenticated' }, status: 401 };
			}
			try {
				const res = await updateVerificationGroup(tokens, id, payload);
				if (!res.error) await fetchData();
				return res;
			} catch (err) {
				const message = err instanceof Error ? err.message : 'Failed to update group';
				return { error: { id: 'UPDATE_FAILED', message }, status: 500 };
			}
		},
		[accessToken, idToken, id, fetchData, refreshTokens]
	);

	return { data, error, loading, refetch: fetchData, update };
};
